'use client';

import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import type { StageCue } from './stage';
import { useStageCue } from './useStageCue';

/**
 * Literal class strings (Tailwind scans source text), one per TONE_TOKENS pair:
 * the chip is `fill` + `text`, the dot is `mark`. __tests__/StageChip.test.tsx
 * asserts all four (both chips, both dots) against TONE_TOKENS, whose pairs
 * stageMarkerContrast.test.ts measures. Not <Badge>: its warning variant (bg-warning/10 text-warning) is
 * 2.02:1 in light. Not lib/status-colors: those are research-node statuses with
 * the same failing pairing.
 */
const CHIP_CLASS = {
  success: 'bg-success text-background',
  warning: 'bg-warning text-warning-foreground',
} as const;
const DOT_CLASS = {
  success: 'bg-success',
  warning: 'bg-warning-strong',
} as const;

/** The cue with its translated chip label and full title, or null on prod. */
export function useStageLabel(): { cue: StageCue; label: string; title: string } | null {
  const t = useTranslations('layout.stageMarker');
  const { cue } = useStageCue();
  if (!cue) return null;
  const label =
    cue.stage === 'development'
      ? t('development')
      : cue.stage === 'preprod'
        ? t('preprod')
        : cue.code;
  const title =
    cue.stage === 'development'
      ? t('developmentTitle')
      : cue.stage === 'preprod'
        ? t('preprodTitle')
        : t('otherTitle', { stage: cue.stage });
  return { cue, label, title };
}

/**
 * The dev/preprod chip beside the logo. Renders nothing on prod.
 *
 * `compact` is the dot for the collapsed sidebar, positioned by the caller's
 * `relative` box. It is decorative (aria-hidden): a button's children are
 * presentational to assistive tech, so the caller puts useStageLabel()'s title
 * in its own accessible name and tooltip instead.
 */
export function StageChip({ compact = false }: { compact?: boolean }) {
  const stage = useStageLabel();
  if (!stage) return null;

  if (compact) {
    return (
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-surface',
          DOT_CLASS[stage.cue.tone],
        )}
      />
    );
  }
  return (
    <span
      title={stage.title}
      className={cn(
        'shrink-0 rounded-sm px-1.5 py-0.5 text-[11px] font-semibold uppercase leading-none tracking-wider',
        CHIP_CLASS[stage.cue.tone],
      )}
    >
      {stage.label}
    </span>
  );
}
